import { useEffect, useRef } from 'react';

/**
 * Custom hook to trap focus within a modal or dialog
 * Ensures keyboard users can't tab out of modal content
 * @param {boolean} isActive - Whether the focus trap should be active
 */
export const useFocusTrap = (isActive) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isActive || !containerRef.current) return;

    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Store the element that had focus before opening
    const previouslyFocusedElement = document.activeElement;

    // Focus the first element when trap activates
    firstElement?.focus();

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);

    // Cleanup: restore focus when trap deactivates
    return () => {
      container.removeEventListener('keydown', handleTabKey);
      previouslyFocusedElement?.focus();
    };
  }, [isActive]);

  return containerRef;
};

/**
 * Custom hook to manage focus on error messages
 * Moves focus to error alert when validation fails
 * @param {boolean} hasError - Whether there's an error to focus
 * @param {Array} dependencies - Dependencies to watch for error changes
 */
export const useFocusOnError = (hasError, dependencies = []) => {
  const errorRef = useRef(null);

  useEffect(() => {
    if (hasError && errorRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, ...dependencies]);

  return errorRef;
};

/**
 * Custom hook to restore focus after an action
 * Useful for delete operations, modal closes, etc.
 * @param {Function} action - The action to perform
 * @param {boolean} shouldRestore - Whether to restore focus
 */
export const useRestoreFocus = () => {
  const previousFocusRef = useRef(null);

  const saveFocus = () => {
    previousFocusRef.current = document.activeElement;
  };

  const restoreFocus = () => {
    previousFocusRef.current?.focus();
  };

  return { saveFocus, restoreFocus };
};

const focusManagementHooks = {
  useFocusTrap,
  useFocusOnError,
  useRestoreFocus
};

export default focusManagementHooks;
